import {} from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import { useTimeEntryActionBarState } from 'components/domain/time-entry/TimeEntryActionBar/TimeEntryActionBarContext';
import useMemberTimeEntryDataReportData, {
  IMemberTimeEntryReportDataFilter,
} from 'components/domain/time-entry/TimeEntryDataReport/MemberTimeEntryDataReport/hooks/useMemberTimeEntryDataReportData';
import MemberTimeEntryDataReport from 'components/domain/time-entry/TimeEntryDataReport/MemberTimeEntryDataReport/MemberTimeEntryDataReport';
import { ITimeEntryDataTableRow } from 'components/domain/time-entry/TimeEntryDataTable/TimeEntryDataTable';
import useTimesheetsColumns from 'containers/timesheets/hooks/useTimesheetsColumns';
import { usePrintComplexDataChannelListener } from 'contexts/ChannelContext/hooks/usePrintComplexDataChannelListener';
import { useReduxSelector } from 'hooks';
import { useLoaderPromise } from 'hooks/promise';
import { first, isEmpty, isNil, isNull, map, partition, uniq } from 'lodash';
import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { updateCheckedTimeEntryIds, updateCheckedTimeOffIds } from 'store/TimeEntryTable/TimeEntryTable';
import { dateUtils } from 'utils';
import { t } from 'utils/localize';
import { useTimeEntryExportChannelListener } from '../hooks/useTimeEntryExportChannelListener';

export interface IMemberTimeEntryTableListeningContainerProps {
  className?: string | object;
  filter: IMemberTimeEntryReportDataFilter;
  scroller: HTMLElement | Document | null;
  applyPrintOptions: boolean;
}

const MemberTimeEntryTableListeningContainer = (props: IMemberTimeEntryTableListeningContainerProps) => {
  const { className, scroller, filter, applyPrintOptions } = props;

  const {
    data,
    onDataChange: onChange,
    loading,
    loadedAll,
    loadAllGroupedData,
  } = useMemberTimeEntryDataReportData(scroller, filter);

  const [timeEntryActionBarState, timeEntryActionBarDispatch] = useTimeEntryActionBarState();

  useEffect(() => {
    if (!isEmpty(timeEntryActionBarState.entries)) {
      const ids = uniq(timeEntryActionBarState.entries.map((entry) => entry.member.id));
      onChange(ids);
    }
    if (!isEmpty(timeEntryActionBarState.memberIds)) {
      onChange(data.map((d) => d.id));
    }
    if (!isNull(timeEntryActionBarState.formData)) {
      const ids = timeEntryActionBarState.formData.members;
      onChange(ids);
    }
  }, [timeEntryActionBarState]);

  const dispatch = useDispatch();
  const selectedIds = useReduxSelector((state) =>
    state.timeEntryTable.checkedTimeEntryIds.concat(state.timeEntryTable.checkedTimeOffIds)
  );
  const { timeEntryColumns } = useTimesheetsColumns();

  const onSelect = useCallback(
    (rows: ITimeEntryDataTableRow[]) => {
      const [timeEntryIds, timeOffIds] = partition(rows, (row) => isNil(row.timeOffType));
      dispatch(updateCheckedTimeEntryIds(map(timeEntryIds, 'id')));
      dispatch(updateCheckedTimeOffIds(map(timeOffIds, 'id')));
    },
    [dispatch]
  );

  const exportWithLoader = useLoaderPromise(loadAllGroupedData, t('Exporting...'));
  const printWithLoader = useLoaderPromise(loadAllGroupedData, t('Preparing to print...'));

  useTimeEntryExportChannelListener(
    data,
    loadedAll,
    `busybusy-${t('activity-report-time-entries')}-${dateUtils.isoTimeStampLocal()}`,
    exportWithLoader
  );
  usePrintComplexDataChannelListener(data, loadedAll, printWithLoader);

  const classes = classNames('member-time-entry-table-container', className);

  return (
    <MemberTimeEntryDataReport
      className={classes}
      columnSettings={timeEntryColumns}
      onSelectChange={data.some((item) => item.canManage) ? onSelect : undefined}
      selectedIds={selectedIds}
      timeRange={filter.timeRange}
      onDataChange={onChange}
      data={data}
      loading={loading}
      loadedAll={loadedAll}
      projectId={first(filter.projectIds)}
      costCodeId={first(filter.costCodeIds)}
      equipmentId={first(filter.equipmentIds)}
      applyPrintOptions={applyPrintOptions}
    />
  );
};

export default MemberTimeEntryTableListeningContainer;
